import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  ArrowLeft, User, Mail, Calendar, Save, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function ProfilePersonal() {
  const navigate = useNavigate();
  const { user, userProfile, updateUserProfile } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    birthDate: "",
  });

  // Load user data
  useEffect(() => {
    if (user || userProfile) {
      setFormData({
        name: user?.displayName || userProfile?.name || "",
        birthDate: userProfile?.birthDate || userProfile?.onboardingData?.data_nascimento?.split('T')[0] || "",
      });
    }
  }, [user, userProfile]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Ei, todo mundo tem um nome! Preenche aí.");
      return;
    }

    setIsSaving(true);
    
    const result = await updateUserProfile({
      name: formData.name.trim(),
      birthDate: formData.birthDate || null,
    });

    setIsSaving(false);

    if (result.success) {
      toast.success("Dados salvos! Agora você é oficialmente atualizado. ✨");
      navigate("/profile");
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafbfc] pb-32">
      <div className="max-w-lg mx-auto px-5 py-6">
        
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-6"
        >
          <Link to="/profile">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 translate="no" className="text-xl font-black tracking-tight text-slate-900">
              Meus Dados
            </h1>
            <p className="text-slate-500 text-sm">
              Não se preocupe, a gente guarda bem.
            </p>
          </div>
        </motion.header>

        {/* Avatar */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center mb-8"
        >
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white text-4xl font-black shadow-lg shadow-indigo-500/30">
            {(formData.name && formData.name.length > 0) ? formData.name.charAt(0).toUpperCase() : "?"}
          </div>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="space-y-5"
        >
          {/* Name */}
          <div className="bg-white rounded-[24px] p-5 shadow-sm border border-slate-100">
            <Label translate="no" className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-indigo-500" />
              Nome completo
            </Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Como você quer ser chamado?"
              className="h-12 rounded-xl bg-slate-50 border-slate-200"
              data-testid="input-name"
            />
          </div>

          {/* Email (read-only) */}
          <div className="bg-white rounded-[24px] p-5 shadow-sm border border-slate-100">
            <Label translate="no" className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
              <Mail className="w-4 h-4 text-indigo-500" />
              E-mail
            </Label>
            <Input
              type="email"
              value={user?.email || ""}
              disabled
              className="h-12 rounded-xl bg-slate-100 border-slate-200 text-slate-500"
            />
            <p translate="no" className="text-xs text-slate-400 mt-2">
              O e-mail não pode ser alterado. É tipo tatuagem, sabe?
            </p>
          </div>

          {/* Birth Date */}
          <div className="bg-white rounded-[24px] p-5 shadow-sm border border-slate-100">
            <Label translate="no" className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-indigo-500" />
              Data de nascimento
            </Label>
            <Input
              type="date"
              value={formData.birthDate}
              onChange={(e) => setFormData(prev => ({ ...prev, birthDate: e.target.value }))}
              className="h-12 rounded-xl bg-slate-50 border-slate-200"
              data-testid="input-birthdate"
            />
            <p className="text-xs text-slate-400 mt-2">
              Prometemos não contar pra ninguém. 🤫
            </p>
          </div>
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mt-8"
        >
          <Button
            className="w-full h-14 rounded-full font-bold bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white shadow-lg shadow-indigo-500/30"
            onClick={handleSave}
            disabled={isSaving}
            data-testid="save-profile"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Salvar alterações
              </>
            )}
          </Button>
        </motion.div>

        {/* Fun fact */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          translate="no"
          className="text-center text-xs text-slate-400 mt-6 italic"
        >
          "Seus dados estão mais seguros que dinheiro embaixo do colchão."
        </motion.p>
      </div>
    </div>
  );
}
